import React, { useState, useEffect, useRef } from 'react';
import { Modal, Backdrop, Fade, IconButton, Box, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import styles from './PaymentIframeModal.module.scss';
import { localStorageHelper } from '../../../utils/localStorageHelper';
import dayjs from 'dayjs';

interface BookingDisplayInfo {
  professionalName?: string;
  selectedDate?: string;
  selectedTime?: string;
  price?: number | string;
}

interface PaymentIframeModalProps {
  open: boolean;
  onClose: () => void;
  url: string | null;
  bookingInfo?: BookingDisplayInfo;
}

const PaymentIframeModal: React.FC<PaymentIframeModalProps> = ({ open, onClose, url, bookingInfo }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const loadCount = useRef(0);
  const [reservationDetails, setReservationDetails] = useState<any>(null);

  useEffect(() => {
    if (open) {
      loadCount.current = 0;
      setLoading(true);

      const stored = localStorageHelper.getItem<any>('bookingDetails') || {};
      const dateStart = stored.date_booking_start || '';
      const parsedDate = stored.selectedDate || (dateStart ? dateStart.split(' ')[0] : '');
      const parsedTime = stored.selectedTime || (dateStart ? dateStart.split(' ')[1] : '');

      setReservationDetails({
        professionalName: bookingInfo?.professionalName || stored.professionalName || 'N/A',
        selectedDate: bookingInfo?.selectedDate || parsedDate || '',
        selectedTime: bookingInfo?.selectedTime || parsedTime || '',
        price: bookingInfo?.price ?? stored.price ?? stored.consult_price ?? 0,
      });
    }
  }, [open, bookingInfo]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (typeof event.data !== 'object' || event.data === null) return;

      if (event.data.type === 'PAYMENT_COMPLETE' && event.data.url) {
        const returnedUrl = event.data.url as string;
        if (returnedUrl.includes('/booking-details/') && returnedUrl.includes('isPayment=1')) {
          window.location.href = returnedUrl;
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onClose]);

  if (!url) return null;

  return (
    <Modal
      open={open}
      onClose={(event, reason) => { if (reason !== 'backdropClick') onClose(); }}
      closeAfterTransition
      disableEnforceFocus
      disableAutoFocus
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        className: styles['payment-modal__backdrop'],
      }}
    >
      <Fade in={open}>
        <Box className={styles['payment-modal__content']}>
          <header className={styles['payment-modal__header']}>
            <div>
              <h3 className={styles['payment-modal__title']}>
                {t('appAdmin.PaymentIframeModal.title', 'Pago seguro')}
              </h3>
              <p>{t('appAdmin.PaymentIframeModal.subtitle', 'Completá los datos de pago para confirmar tu reserva.')}</p>
            </div>
            <IconButton
              className={styles['payment-modal__close-button']}
              onClick={onClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </header>

          <div className={styles['payment-modal__body']}>
            <div className={styles['payment-modal__iframe-wrapper']}>
              {loading && (
                <div className={styles['payment-modal__loader']}>
                  <CircularProgress size={48} color="primary" />
                </div>
              )}
              <iframe
                src={url}
                className={styles['payment-modal__iframe']}
                onLoad={() => {
                  setLoading(false);
                  loadCount.current += 1;
                }}
                title="Payment Gateway"
                allow="payment"
              />
            </div>

            <aside className={styles['payment-modal__summary']}>
              <div className={styles['payment-modal__summary-card']}>
                <h4>{t('appAdmin.PaymentIframeModal.summaryTitle', 'Resumen de reserva')}</h4>

                <div className={styles['payment-modal__summary-list']}>
                  <div className={styles['payment-modal__summary-row']}>
                    <span>{t('appAdmin.PaymentIframeModal.professional', 'Profesional')}</span>
                    <strong>{reservationDetails?.professionalName}</strong>
                  </div>

                  <div className={styles['payment-modal__summary-row']}>
                    <span>{t('appAdmin.PaymentIframeModal.date', 'Fecha')}</span>
                    <strong>
                      {reservationDetails?.selectedDate ? dayjs(reservationDetails.selectedDate).format('D MMMM YYYY') : 'N/A'}
                    </strong>
                  </div>

                  <div className={styles['payment-modal__summary-row']}>
                    <span>{t('appAdmin.PaymentIframeModal.time', 'Horario')}</span>
                    <strong>
                      {reservationDetails?.selectedTime ? dayjs(`2000-01-01 ${reservationDetails.selectedTime}`).format('h:mm A') : 'N/A'}
                    </strong>
                  </div>

                  <hr className={styles['payment-modal__summary-divider']} />

                  <div className={`${styles['payment-modal__summary-row']} ${styles['payment-modal__summary-row--total']}`}>
                    <span>{t('appAdmin.PaymentIframeModal.total', 'Total')}</span>
                    <strong>$U {reservationDetails?.price}</strong>
                  </div>
                </div>

                <div className={styles['payment-modal__summary-secure']}>
                  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span>{t('appAdmin.PaymentIframeModal.securePayment', 'El pago se procesa a través de una pasarela externa segura.')}</span>
                </div>
              </div>
            </aside>
          </div>
        </Box>
      </Fade>
    </Modal>
  );
};

export default PaymentIframeModal;